import { BatchLinkPlugin } from '@orpc/client/plugins';
import { FEATURE_FLAGS } from '@erp/shared-types';
import { RPC_BATCH_MAX_SIZE } from '@erp/rpc';
import { isBooleanFeatureFlagEnabled } from 'src/integrations/feature-flags/feature-flags';

export function isBatchableRpcMethod(method: string): boolean {
  return method === 'GET';
}

/**
 * Procedures kept out of every batch.
 *
 * Batching trades round trips for coupled latency: a batch resolves only when
 * its slowest member does, so fusing a fast read with a slow one drags the fast
 * one down to the slow one's latency. That's a good trade for many small reads
 * and a bad one for these — each scans or counts the whole filtered product set,
 * and the AS/400 view additionally expands every matching product's raw ingest
 * records.
 *
 * The item catalog dispatches several of them in one commit (the product page,
 * the AS/400 grid's page, and the exact total), which is exactly the case where
 * batching turns independent waits into one long one. The page is built to show
 * each result the moment it lands — rows first, the exact total whenever it
 * catches up — and that only holds if they travel separately.
 *
 * Matched on the procedure path, so the RPC base URL stays irrelevant.
 */
const UNBATCHED_PROCEDURE_PATHS = [
  'inventoryManagement/products/listCatalog',
  'inventoryManagement/products/listCatalogAs400View',
  'inventoryManagement/products/countCatalog',
  // The forecast grid and its scope-sized aggregate render independently.
  // A batch would make the grid wait for the aggregate on broad SKU filters.
  'procurement/forecastRuns/demandMatrix',
  'procurement/forecastRuns/demandSummary',
  // The buying board's rows: batched with the filter options they waited on
  // the slower options query (2.6s list, 3.9s response on staging).
  'procurement/replenishmentSuggestions/listBuyingOrders',
] as const;

export function isBatchableRpcProcedure(url: URL | string): boolean {
  const { pathname } = typeof url === 'string' ? new URL(url) : url;
  return !UNBATCHED_PROCEDURE_PATHS.some((path) =>
    pathname.endsWith(`/${path}`),
  );
}

export function isRpcBatchingEnabled(): boolean {
  return isBooleanFeatureFlagEnabled(
    FEATURE_FLAGS['orpc-batching-enabled'],
    false,
  );
}

export function createRpcBatchLinkPlugin() {
  return new BatchLinkPlugin({
    groups: [
      {
        condition: ({ request }) =>
          isBatchableRpcMethod(request.method) &&
          isBatchableRpcProcedure(request.url) &&
          isRpcBatchingEnabled(),
        context: {},
      },
    ],
    maxSize: RPC_BATCH_MAX_SIZE,
  });
}
